import CloseOutlined from '@mui/icons-material/CloseOutlined';
import { Box, IconButton, Typography } from '@mui/material';
import { isTechnologyRequired } from 'domain/shared/career';
import { TextInput } from 'framework/form';
import { OverflowTooltip } from 'framework/ui/ui-builder';
import type { CreateEditFormSection } from 'framework/ViewTypes/contentLayout';
import type { SourcingFilterKey, SourcingFilterOptions, SourcingFilters } from '../../../../types';
import { SourcingSearchMode } from '../../../../types';
import { SOURCING_FILTER_KEYS, SOURCING_FILTER_LABELS, SOURCING_FILTERS } from '../../constants';
import { getSourcingFilterValue } from '../../utils/utils';
import { findSourcingSpecialization, getSourcingFilterMode } from '../../utils/sourcingFilterModel';

type Props = {
  selectedParameters: SourcingFilters;
  filterOptions: SourcingFilterOptions;
  criteriaError: string;
  onRemoveFilter: (key: SourcingFilterKey) => void;
};

const getSaveSourcingFilterDrawerSections = ({
  selectedParameters,
  filterOptions,
  criteriaError,
  onRemoveFilter,
}: Props): CreateEditFormSection[] => {
  const rows = SOURCING_FILTER_KEYS.flatMap(key => {
    const value = getSourcingFilterValue(key, selectedParameters, filterOptions);
    return value
      ? [{ key, label: SOURCING_FILTER_LABELS[key], value, mode: getSourcingFilterMode(key, selectedParameters) }]
      : [];
  });
  const specialization = findSourcingSpecialization(filterOptions, selectedParameters.specializationId);
  const technologyRequired = isTechnologyRequired(specialization?.specializationId);

  return [
    {
      id: 'name',
      fields: [
        {
          id: 'filterName',
          size: { xs: 12, md: 8, xl: 8 },
          content: <TextInput source="filterName" label="Filter name" />,
        },
      ],
    },
    {
      id: 'criteria',
      title: 'Selected criteria',
      content: (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, minmax(0, 3fr))',
            gap: 0.5,
            minWidth: 0,
          }}
        >
          {criteriaError && (
            <Typography variant="caption" color="error" sx={{ gridColumn: '1 / -1' }}>
              {criteriaError}
            </Typography>
          )}
          {rows.map(row => (
            <Box key={row.key} sx={{ display: 'contents' }}>
              <Typography variant="subtitle2" noWrap sx={{ gridColumn: 1, minWidth: 0 }}>
                {row.label}:
              </Typography>
              <OverflowTooltip>
                <Typography variant="body2" noWrap sx={{ gridColumn: 2, minWidth: 0 }}>
                  {row.value}
                </Typography>
              </OverflowTooltip>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, gridColumn: 3, minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ minWidth: 70 }}>
                  {row.mode === SourcingSearchMode.STRICT ? 'strict filter' : 'non-strict filter'}
                </Typography>
                {row.key !== SOURCING_FILTERS.specialization &&
                  (row.key !== SOURCING_FILTERS.technology || !technologyRequired) && (
                    <IconButton size="small" onClick={() => onRemoveFilter(row.key)}>
                      <CloseOutlined sx={{ fontSize: 14 }} color="primary" />
                    </IconButton>
                  )}
              </Box>
            </Box>
          ))}
        </Box>
      ),
    },
  ];
};

export default getSaveSourcingFilterDrawerSections;
